import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Typography, message } from "antd";
import BookingPoster from "./BookingPoster";
import {
    downloadBookingPosterFromElement,
    ensureBookingPosterFontsLoaded,
    getBookingPosterImage,
} from "../../utils/bookingPoster";
import { useIsMobileDeliveryDevice } from "../../utils/device";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

function BookingPosterPanel({ booking, publicUrl }) {
    const { t } = useTranslation();
    const isMobile = useIsMobileDeliveryDevice();
    const posterRef = useRef(null);
    const [posterReady, setPosterReady] = useState(false);
    const [posterError, setPosterError] = useState("");
    const [generating, setGenerating] = useState(false);
    const [savedImageUrl, setSavedImageUrl] = useState("");

    useEffect(() => {
        if (!booking) {
            setPosterReady(false);
            return;
        }
        setPosterReady(false);
        setPosterError("");
        let cancelled = false;

        (async () => {
            try {
                await ensureBookingPosterFontsLoaded();
            } catch (error) {
                console.error("Booking poster font loading failed:", error);
            } finally {
                if (!cancelled) {
                    setPosterReady(true);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [booking]);

    const handleDownloadPoster = useCallback(async () => {
        if (!posterRef.current || !booking || generating) {
            return;
        }
        setGenerating(true);
        try {
            if (isMobile) {
                const { dataUrl } = await getBookingPosterImage(posterRef.current, booking.title);
                setSavedImageUrl(dataUrl);
            } else {
                await downloadBookingPosterFromElement(posterRef.current, booking.title);
                message.success(t("booking.poster.downloaded"));
            }
        } catch (error) {
            console.error("Booking poster generation failed:", error);
            setPosterError(t("booking.poster.failed"));
            message.error(t("booking.poster.downloadFailed"));
        } finally {
            setGenerating(false);
        }
    }, [booking, isMobile, generating, t]);

    const handleCloseSavedImage = useCallback(() => {
        setSavedImageUrl("");
    }, []);

    if (!booking) {
        return null;
    }

    return (
        <div className="booking-preview">
            <div className="booking-preview__actions">
                <Button
                    type="primary"
                    onClick={handleDownloadPoster}
                    loading={generating}
                    disabled={!posterReady || Boolean(posterError)}
                >
                    {isMobile ? t("booking.poster.openPoster") : t("booking.poster.downloadPoster")}
                </Button>
            </div>

            <div className="booking-preview__poster-wrap">
                {!posterReady && (
                    <p className="booking-preview__poster-status">{t("booking.poster.loading")}</p>
                )}
                {posterError && (
                    <Paragraph type="danger" className="booking-preview__poster-status">
                        {posterError}
                    </Paragraph>
                )}
                <div className={posterReady ? "" : "booking-preview__poster--loading"}>
                    <BookingPoster ref={posterRef} booking={booking} publicUrl={publicUrl} />
                </div>
            </div>

            {savedImageUrl && (
                <div
                    className="booking-preview__overlay"
                    role="dialog"
                    aria-modal="true"
                    onClick={handleCloseSavedImage}
                >
                    <p className="booking-preview__overlay-hint">{t("booking.poster.longPressHint")}</p>
                    <img
                        className="booking-preview__overlay-img"
                        src={savedImageUrl}
                        alt={booking.title}
                        onClick={(event) => event.stopPropagation()}
                    />
                    <Button className="booking-preview__overlay-close" onClick={handleCloseSavedImage}>
                        {t("booking.poster.closePreview")}
                    </Button>
                </div>
            )}
        </div>
    );
}

export default BookingPosterPanel;
